import Enumerable from "linq";
import store from "../../../store";
import { BrowserKinds, BrowserUtility, UrlSchemeClient } from "../../../utilities";
import { Rectangle, Point, Size } from "../../../domain/drawing";
import { LoggerFactory } from "../../../utilities/logger";
import { WindowsOpenRequest } from "../../../domain/localhost/items";
import { DomainRegistry } from "../../../domain";
import { Dictionary } from "../../../domain/system";

export class WindowOpener {
  static open(from: string, to: string, parameters: { [key: string]: string } = {}): void {
    if (this.shouldRequest(from, to)) {
      this.requestOpenWindow(WindowOpener.toUrl(to, this.toBase64EncodableParameters(parameters)), true);
    } else {
      // ブラウザを直接開く場合はURLエンコードしてなくても開けるので無理に変更はしない.
      this.openWindow(WindowOpener.toUrl(to, parameters));
    }
  }

  static requestOpen(location: string, parameters: { [key: string]: string } = {}): void {
    this.requestOpenWindow(WindowOpener.toUrl(location, this.toBase64EncodableParameters(parameters)), false);
  }

  static toUrlEncodedUrl(location: string, parameters: { [key: string]: string }): string {
    const url = this.toUrl(location, this.toUrlEncodedParameters(parameters));
    const additionalQueries = this.getAdditionalQueries();
    return this.jointUrl(url, ...additionalQueries);
  }

  private static toBase64EncodableParameters(parameters: { [key: string]: string }): { [key: string]: string } {
    return Enumerable.from(Object.keys(parameters))
      .aggregate<{ [key: string]: string }>({}, (s, k) => {
        s[k] = this.toBase64EncodableValue(parameters[k]);
        return s;
      });
  }

  private static toUrlEncodedParameters(parameters: { [key: string]: string }): { [key: string]: string } {
    return Enumerable.from(Object.keys(parameters))
      .aggregate<{ [key: string]: string }>({}, (s, k) => {
        // undefinedが文字列となってしまわないように対策している
        s[k] = !!parameters[k] ? encodeURIComponent(parameters[k]) : parameters[k];
        return s;
      });
  }

  private static toBase64EncodableValue(value: string): string {
    // マルチバイト文字列はbtoa()で例外発生するので、事前にbtoa()して例外発生する場合はマルチバイト文字列と見做してURLエンコードする.
    try {
      btoa(value);
      return value;
    } catch {
      return encodeURIComponent(value);
    }
  }

  static shouldRequest(from: string, to: string): boolean {
    this._logger.debug(from + " -> " + to);
    const configuration = store.state.configuration.behavior.localhost.window.opener;
    return configuration.isEnable
        && (!configuration.restrictions.browser.isEnable || (BrowserUtility.getBrowserKind() === BrowserKinds.InternetExplorer))
        && (!configuration.restrictions.path.isEnable || Enumerable.from(configuration.restrictions.path.paths).any(p => (p.to === to) && (p.from === from)));
  }

  private static requestOpenWindow(url: string, shouldAddCode: boolean): void {
    this._logger.debug("AssistantへWindowのOpenを要求します.");
    this._logger.debug(url);
    const client = new UrlSchemeClient();
    const additionalQueries = shouldAddCode
      ? this.getAdditionalQueries()
      : [];
    client.request(this.toDocuMakerSchemeUri(this.jointUrl(url, ...additionalQueries)));
  }

  private static getAdditionalQueries(): string[] {
    return [this.getLoginUserCode(), this.getLoginDepartmentCode(), this.getOrderUserCode(), this.getAttendingUserCode(), this.getClientIpAddress(), this.getHostName(), this.getLoginDepartmentGroupCode(), this.getReferenceableDepartmentGroupCodes()];
  }

  private static toDocuMakerSchemeUri(url: string): string {
    return store.state.configuration.behavior.localhost.schemeUrlPrefix
      + "?action=open&url="
      // 独自のdocumakerスキームとはいえ、URIなのだから値にURIの予約文字が入っちゃダメ.
      // よってbase64エンコードした値を設定するのは本来NG.
      // (base64エンコード結果としてとりうる「+」と「/」はURIの予約文字なので.)
      // 実際「+」が入ったデータを送るとAsssitant側では解釈できない.(「/」は許されてる模様.詳細は後述.)
      // なのだが、各パラメータが正しくURLエンコードされた正規のURLであればギリギリの綱渡りで成立してる模様.
      // ・base64エンコード結果として取りうる文字でURI上許容できないのは「+」(111110b)と「/」(111111b).
      // ・ここでbase64エンコードする対象のURLが正しくURLエンコードされていればUS-ASCIIの文字以外は入りえない.
      // ・US-ASCIIの場合最上位bitは必ず0なのでどの位置の6bitを取ったとしても問題となりうる文字は以下の4つのみ.
      //   ・「+」(111110b)になりえるのは「>」(3Eh)と「~」(7Eh)の2つ.
      //   ・「/」(111111b)になりえるのは「?」(3Fh)と制御文字のDEL(7Fh).
      // ・前述の4文字の内httpのURLとしてありえるのは「?」のみだが、幸いAssistantは解釈できた.
      //   ・AssistantはdocumakerスキームのURIのQuery(?以降)をHttpUtility.ParseQueryString()で分割して抽出している.
      //   ・HttpUtility.ParseQueryString()の挙動として「+」は半角スペースに分割されるが「/」はそのまま.
      //     ->先頭を除けば「?」を取ることはないはずなのでたまたま見逃されてる？
      //     ->HttpUtility.ParseQueryString()の挙動が変更される可能性は極めて低い.(互換性維持のためにも難しいと思われる.)
      // 本来であればbase64以外のやり方に見直すべきだが、以下の理由からここは現状を維持する.
      // ・本体とAssistantを同時に更新しないと運用中システムは対応できないが、Webアプリと配信なので完全には難しい.
      //   ->やるなら別のやり方用の新しいactionを定義して両対応を維持した上で先行配信する等めんどくさい対策が必要.(もしくはシステム停止...)
      // ・対象が正しくURLエンコードされたURLであれば上記理由でギリギリ破綻しない.
      // 今後追加するactionで同じような対処が必要な場合はbase64は採用しない事.
      + btoa(url);
  }

  static jointUrl(url: string, ...additionalQueries: string[]): string {
    const queries = additionalQueries.length > 0
      ? (url.match(/\?/) ? "&" : "?") + Enumerable.from(additionalQueries).where(q => !!q).toArray().join("&")
      : "";
    return url + queries;
  }

  static getLoginUserCode(): string {
    const stored = store.state.user;
    return stored
      ? "userCode=" + stored.id
      : null;
  }

  static getLoginDepartmentCode(): string {
    const stored = store.state.department;
    return stored
      ? "departmentCode=" + stored.id
      : null;
  }

  static getOrderUserCode(): string {
    const stored = store.state.orderUser;
    return stored
      ? "orderUserCode=" + stored.id
      : null;
  }

  static getAttendingUserCode(): string {
    const stored = store.state.attendingUser;
    return stored
      ? "attendingUserCode=" + stored.id
      : null;
  }

  static getClientIpAddress(): string {
    const stored = store.state.clientIpAddress;
    return stored
      ? "clientIpAddress=" + stored
      : null;
  }

  static getHostName(): string {
    const stored = store.state.hostName;
    return stored
      ? "hostName=" + encodeURIComponent(stored)
      : null;
  }

  static getLoginDepartmentGroupCode(): string {
    const stored = store.state.departmentGroup;
    return stored && !this.shouldLaunchWithEmptyDepartmentGroup()
      ? "departmentGroupCode=" + stored.id
      : null;
  }

  private static shouldLaunchWithEmptyDepartmentGroup(): boolean {
    return store.state.departmentGroup
      && store.state.configuration.behavior.departmentGroup.scope.isEnable
      && !store.state.isDepartmentGroupScopeEnable;
  }

  static getReferenceableDepartmentGroupCodes(): string {
    const stored = store.state.referenceableDepartmentGroups;
    return (stored && stored.length !== 0)
      ? "referenceableDepartmentGroupCodes=" + stored.map(g => g.id)
      : null;
  }

  static openWindow(url: string): WindowProxy {
    this._logger.debug("新しいWindowをopenします.");
    this._logger.debug(url);
    const features = this.getFeatures();
    this._logger.debug(features);
    const openedWindow = window.open(url, "", features);
    this.locate(openedWindow);
    return openedWindow;
  }

  private static locate(w: Window): void {
    const rectangle = this.toLocatorRectangle();
    this._logger.debug(
      (this.shouldLocateBasedOnFullScreen ? "openしたwindowを再配置します." : "openしたwindowを自windowと同様に再配置します.")
      + "(" + rectangle.x + "," + rectangle.y + ")"
      + rectangle.width + "x" + rectangle.height
    );
    w.moveTo(rectangle.x, rectangle.y);
    w.resizeTo(rectangle.width, rectangle.height);
  }

  private static toLocatorRectangle(): Rectangle {
    const result = this.shouldLocateBasedOnFullScreen
      ? this.calculateLocatorRectangle()
      : new Rectangle(
        new Point(window.screenX, window.screenY),
        new Size(window.outerWidth, window.outerHeight)
      );
    const offsets = store.state.configuration.system.window.opener.offsets;
    return new Rectangle(
      new Point(
        Math.round(result.x + offsets.left),
        Math.round(result.y + offsets.top)
      ),
      new Size(
        Math.round(result.width + offsets.width),
        Math.round(result.height + offsets.height)
      )
    );
  }

  private static get shouldLocateBasedOnFullScreen(): boolean {
    return store.state.configuration.system.window.opener.locatorBasedOnFullScreen.isEnable;
  }

  private static getFeatures(): string {
    const configuration = store.state.configuration.system.window.opener;
    const featureSets = Enumerable.from([
      configuration.features,
      this.getLocatorFeatures(),  // IEのwindow.open()は戻るのが遅くその間小さいサイズでwindow表示されてしまい見栄えが悪いためそれなりの大きさで配置指定する.
    ]).where(f => f && (f !== "")).toArray();
    return featureSets.join(",");
  }

  private static getLocatorFeatures(): string {
    return this.formatLocatorFeatures(this.toLocatorRectangle());
  }

  private static formatLocatorFeatures(source: Rectangle): string {
    // outerWidth/outerHeightを指定したかったがIE非対応.仮指定用にwidth/heightで指定しておく.
    return "top=" + source.y
        + ",left=" + source.x
        + ",width=" + source.width
        + ",height=" + source.height;
  }

  private static calculateLocatorRectangle(): Rectangle {
    const configuration = store.state.configuration.system.window.opener.locatorBasedOnFullScreen;
    const rates = configuration.rates;
    return new Rectangle(
      new Point(
        Math.round(screen.availWidth * rates.left),
        Math.round(screen.availHeight * rates.top)
      ),
      new Size(
        Math.round(screen.availWidth * rates.width),
        Math.round(screen.availHeight * rates.height)
      )
    );
  }

  static toUrl(location: string, parameters: { [key: string]: string }): string {
    return WindowOpener.toApplicationPath(location) + WindowOpener.toParametersAsString(parameters);
  }

  private static toApplicationPath(location: string): string {
    if (location.substring(0, 4) === "http") {
      return location;
    }
    if (location.indexOf(process.env.ASSET_PATH) === 0) {
      return location;
    }
    return location.substring(0, 1) === "/"
      ? process.env.ASSET_PATH + location.substring(1)
      : process.env.ASSET_PATH + location;
  }

  private static toParametersAsString(parameters: { [key: string]: string }): string {
    const expressions = WindowOpener.toParameterExpressions(parameters);
    return (0 < expressions.length)
      ? "?" + expressions.join("&")
      : "";
  }

  private static toParameterExpressions(parameters: { [key: string]: string }): string[] {
    const results = [];
    for (const key of Object.keys(parameters)) {
      const value = parameters[key];
      if (!value || (value === "")) {
        continue;
      }
      results.push(key + "=" + parameters[key]);
    }
    return results;
  }

  static async requestOpenWindows(request: WindowsOpenRequest): Promise<void> {
    const service = DomainRegistry.localhost.getApplicationService();
    const params = new Dictionary<string, string>();
    params.set("action", "openWindows");
    await service.launchWithKey(null, params, request);
  }

  private static readonly _logger = LoggerFactory.getLogger();
}
